import {
  ActionMode,
  ActionSource,
  getSelectionsOfType,
  Hover,
  HoverType,
  Selection,
  SelectionType,
} from 'app/modules/editor/model/actionmode';
import { autoFixPathBlocks } from 'app/modules/editor/components/canvas/pathKeyframes';
import { LayerUtil, MorphableLayer, VectorLayer } from 'app/modules/editor/model/layers';
import { Path, PathMutator, PathUtil } from 'app/modules/editor/model/paths';
import { PathAnimationBlock } from 'app/modules/editor/model/timeline';
import { AutoAwesome } from 'app/modules/editor/scripts/algorithms';
import { bugsnagClient } from 'app/modules/editor/scripts/bugsnag';
import {
  getImportMorphOffer,
  getMorphBlockRefusal,
  morphIntoLayer,
  morphLayerSets,
  UnfixedMorphError,
} from 'app/modules/editor/scripts/common/morphLayers';
import type { LayerDocument } from 'app/modules/editor/scripts/common/pathOpLayers';
import { Action, State, Store } from 'app/modules/editor/store';
import {
  SetActionMode,
  SetActionModeHover,
  SetActionModeSelections,
  SetPairedSubPaths,
  SetUnpairedSubPath,
} from 'app/modules/editor/store/actionmode/actions';
import {
  getActionMode,
  getActionModeHover,
  getActionModeSelections,
  getPairedSubPaths,
  getUnpairedSubPath,
  isActionMode,
} from 'app/modules/editor/store/actionmode/selectors';
import { BatchAction } from 'app/modules/editor/store/batch/actions';
import { SetSelectedLayers, SetVectorLayer } from 'app/modules/editor/store/layers/actions';
import { getCurrentTime } from 'app/modules/editor/store/playback/selectors';
import { createSelector } from 'app/modules/editor/store/selectors';
import {
  SelectAnimation,
  SetAnimation,
  SetSelectedBlocks,
} from 'app/modules/editor/store/timeline/actions';
import { getSingleSelectedPathBlock } from 'app/modules/editor/store/timeline/selectors';
import { IsolateUndoStep } from 'app/modules/editor/store/undoredo/actions';
import { findIndex, isEqual, remove } from 'lodash-es';
import { Subscription } from 'rxjs';
import { LayerTimelineService } from './layertimeline.service';
import { Duration, type SnackBar, SnackBarService } from './snackbar.service';

// True when action mode has lost the block it edits. The timeline is disabled in action mode, so
// that mostly happens through undo and redo.
const hasLostEditedBlock = createSelector(
  [isActionMode, getSingleSelectedPathBlock],
  (actionMode, block) => actionMode && !block,
);

/**
 * A simple service that provides an interface for making action mode changes.
 */
export class ActionModeService {
  private readonly subscription = new Subscription();
  // The snackbar with the last import's offer to morph (offerImportMorph).
  private importMorphSnackBar: SnackBar | undefined;

  // The services are created once, outside of React (src/main.tsx), so StrictMode can't subscribe
  // twice.
  constructor(
    private readonly store: Store<State>,
    private readonly layerTimelineService: LayerTimelineService,
    private readonly snackBarService: SnackBarService,
  ) {
    this.subscription.add(
      store.select(hasLostEditedBlock).subscribe(hasLost => {
        if (hasLost) {
          // The canvases and the toolbar have nothing to show without the block.
          store.dispatch(new SetActionMode(ActionMode.None));
        }
      }),
    );
  }

  dispose() {
    this.subscription.unsubscribe();
  }

  // Action mode.

  /**
   * Selects the path block and edits its morph in action mode, which moves the current time to
   * the block's start (LayerTimelineController). Returns false, and changes nothing, if the block
   * isn't a path block, or if one of its paths is empty, which says so.
   */
  editMorph(blockId: string) {
    const block = this.layerTimelineService.getAnimation().blocks.find(b => b.id === blockId);
    if (!(block instanceof PathAnimationBlock)) {
      console.warn(`Block ${blockId} isn't a path block, so it has no morph to edit`);
      return false;
    }
    const refusal = getMorphBlockRefusal(block);
    if (refusal) {
      this.snackBarService.show(refusal, 'Dismiss', Duration.Short);
      return false;
    }
    if (this.isActionMode()) {
      if (this.getActivePathBlock()?.id === blockId) {
        return true;
      }
      // The selections, hover, and pairings refer to the other block's paths.
      this.setActionMode(ActionMode.None);
    }
    this.layerTimelineService.selectBlock(blockId, true);
    this.setActionMode(ActionMode.Selection);
    return true;
  }

  /**
   * Morphs one path into another, deleting the other one, as one undo step
   * (scripts/common/morphLayers.ts), and edits the morph in action mode. If it can't, it says why
   * in a snackbar. Returns whether it morphed.
   */
  morphInto(fromId: string, toId: string) {
    const document = this.layerTimelineService.getDocument();
    return this.commitMorph(
      document,
      morphIntoLayer(document, fromId, toId, this.queryStore(getCurrentTime)),
    );
  }

  /**
   * Like morphInto, for the layers of two imported files, whose paths are combined into one path
   * on each side first (morphLayerSets).
   */
  morphLayerSets(fromIds: ReadonlyArray<string>, toIds: ReadonlyArray<string>) {
    const document = this.layerTimelineService.getDocument();
    return this.commitMorph(
      document,
      morphLayerSets(document, fromIds, toIds, this.queryStore(getCurrentTime)),
    );
  }

  /** Saves the morph made from the document as one undo step, or says why there's none. */
  private commitMorph(document: LayerDocument, morphed: ReturnType<typeof morphIntoLayer>) {
    if ('reason' in morphed) {
      this.snackBarService.show(morphed.reason, 'Dismiss', Duration.Long);
      return false;
    }
    if (morphed.autoFixError) {
      // The morph is kept as it is, and action mode says what doesn't match. Paths that auto fix
      // can't match up aren't a crash, so only a thrown error is reported.
      if (!(morphed.autoFixError instanceof UnfixedMorphError)) {
        bugsnagClient.notify(morphed.autoFixError);
      }
      this.snackBarService.show("Couldn't auto fix these paths", 'Dismiss', Duration.Long);
    }
    if (this.isActionMode()) {
      // Its selections, hover, and pairings are for the other block.
      this.setActionMode(ActionMode.None);
    }
    const { vectorLayer, animation } = morphed.document;
    const removedIds = LayerUtil.runPreorderTraversal(document.vectorLayer)
      .map(l => l.id)
      .filter(id => !vectorLayer.findLayerById(id));
    this.store.dispatch(
      new BatchAction(
        new IsolateUndoStep(),
        new SetVectorLayer(vectorLayer),
        new SetAnimation(animation),
        ...this.layerTimelineService.buildCleanupLayerIdActions(...removedIds),
        new SelectAnimation(false),
        new SetSelectedLayers(new Set()),
        new SetSelectedBlocks(new Set([morphed.blockId])),
      ),
    );
    return this.editMorph(morphed.blockId);
  }

  /**
   * After an import, offers to morph what was there into what was imported, or one file into
   * the other, in a snackbar, if that would work (getImportMorphOffer). Returns whether it did,
   * so the caller can say what it imported otherwise.
   *
   * @param before the vector layer before the import
   * @param importedIds the top-level layers each file added, from importLayers
   * @param fileNames the files' names, in the same order, if they came from files
   */
  offerImportMorph(
    before: VectorLayer,
    importedIds: ReadonlyArray<ReadonlyArray<string>>,
    fileNames?: ReadonlyArray<string>,
  ) {
    const offer = getImportMorphOffer(
      before,
      this.layerTimelineService.getDocument(),
      importedIds,
      this.queryStore(getCurrentTime),
      fileNames,
    );
    const { snackBarService } = this;
    if (!offer) {
      // An earlier offer would morph into the layers imported before, so it goes.
      if (this.importMorphSnackBar && snackBarService.getSnackBar() === this.importMorphSnackBar) {
        snackBarService.dismiss();
      }
      this.importMorphSnackBar = undefined;
      return false;
    }
    snackBarService.show(offer.message, 'Morph', Duration.Long, () =>
      this.morphLayerSets(offer.fromIds, offer.toIds),
    );
    this.importMorphSnackBar = snackBarService.getSnackBar();
    return true;
  }

  /**
   * Auto fixes the path blocks that don't morph, and the rest of the chains of morphs they're in
   * (autoFixPathBlocks), as one undo step. It says so if auto fix fails.
   */
  autoFixBlocks(blockIds: Iterable<string>) {
    const document = this.layerTimelineService.getDocument();
    let fixed: typeof document;
    try {
      fixed = autoFixPathBlocks(document, new Set(blockIds));
    } catch (e) {
      bugsnagClient.notify(e instanceof Error ? e : String(e));
      this.snackBarService.show("Couldn't auto fix these paths", 'Dismiss', Duration.Long);
      return;
    }
    if (fixed === document) {
      return;
    }
    this.store.dispatch(
      new BatchAction(
        new IsolateUndoStep(),
        new SetVectorLayer(fixed.vectorLayer),
        new SetAnimation(fixed.animation),
      ),
    );
  }

  isActionMode() {
    return this.getActionMode() !== ActionMode.None;
  }

  getActionMode() {
    return this.queryStore(getActionMode);
  }

  setActionMode(mode: ActionMode) {
    this.store.dispatch(new SetActionMode(mode));
  }

  toggleSplitCommandsMode() {
    this.toggleActionMode(ActionMode.SplitCommands);
  }

  toggleSplitSubPathsMode() {
    this.toggleActionMode(ActionMode.SplitSubPaths);
  }

  togglePairSubPathsMode() {
    this.toggleActionMode(ActionMode.PairSubPaths);
  }

  private toggleActionMode(modeToToggle: ActionMode) {
    const currentMode = this.getActionMode();
    if (currentMode === ActionMode.None) {
      return;
    }
    this.setActionMode(currentMode === modeToToggle ? ActionMode.Selection : modeToToggle);
  }

  isShowingSubPathActionMode() {
    return this.isShowingActionModeType(SelectionType.SubPath);
  }

  isShowingSegmentActionMode() {
    return this.isShowingActionModeType(SelectionType.Segment);
  }

  isShowingPointActionMode() {
    return this.isShowingActionModeType(SelectionType.Point);
  }

  private isShowingActionModeType(type: SelectionType) {
    const mode = this.getActionMode();
    return (
      mode !== ActionMode.None &&
      (mode !== ActionMode.Selection || this.getSelections().filter(s => s.type === type).length)
    );
  }

  closeActionMode() {
    const mode = this.getActionMode();
    if (mode === ActionMode.None) {
      return;
    }
    if (mode === ActionMode.Selection) {
      if (this.queryStore(getActionModeSelections).length) {
        // TODO: move this logic out into a component (it's confusing)
        this.store.dispatch(new SetActionModeSelections([]));
      } else {
        this.store.dispatch(new SetActionMode(ActionMode.None));
      }
    } else {
      this.store.dispatch(new SetActionMode(ActionMode.Selection));
    }
  }

  // Selections.

  setSelections(selections: ReadonlyArray<Selection>) {
    this.store.dispatch(new SetActionModeSelections(selections));
  }

  getSelections() {
    return this.queryStore(getActionModeSelections);
  }

  toggleSubPathSelection(source: ActionSource, subIdx: number) {
    const selections = [...this.getSelections()];
    remove(selections, s => s.type !== SelectionType.SubPath || s.source !== source);
    const type = SelectionType.SubPath;
    const toggledSelections = this.toggleSelections(selections, [{ type, source, subIdx }]);
    this.store.dispatch(new SetActionModeSelections(toggledSelections));
  }

  toggleSegmentSelections(
    source: ActionSource,
    segments: ReadonlyArray<{ subIdx: number; cmdIdx: number }>,
  ) {
    const selections = [...this.getSelections()];
    remove(selections, s => s.type !== SelectionType.Segment || s.source !== source);
    const type = SelectionType.Segment;
    const toggledSelections = this.toggleSelections(
      selections,
      segments.map(({ subIdx, cmdIdx }) => ({ type, source, subIdx, cmdIdx })),
    );
    this.store.dispatch(new SetActionModeSelections(toggledSelections));
  }

  togglePointSelection(
    source: ActionSource,
    subIdx: number,
    cmdIdx: number,
    isShiftOrMetaPressed: boolean,
  ) {
    const selections = [...this.getSelections()];
    remove(selections, s => s.type !== SelectionType.Point || s.source !== source);
    const type = SelectionType.Point;
    const toggledSelections = this.toggleSelections(
      selections,
      [{ type, source, subIdx, cmdIdx }],
      isShiftOrMetaPressed,
    );
    this.store.dispatch(new SetActionModeSelections(toggledSelections));
  }

  /**
   * Toggles the specified shape shifter selections. If a selection exists, all selections
   * will be removed from the list. Otherwise, they will be added to the list of selections.
   * By default, all other selections from the list will be cleared.
   */
  private toggleSelections(
    currentSelections: Selection[],
    newSelections: Selection[],
    appendToList = false,
  ) {
    const matchingSelections = remove(currentSelections, currSel => {
      // Remove any selections that are equal to a new selection.
      return newSelections.some(s => isEqual(s, currSel));
    });
    if (!matchingSelections.length) {
      // If no selections were removed, then add all of the selections to the list.
      currentSelections.push(...newSelections);
    }
    if (!appendToList) {
      // If we aren't appending multiple selections at a time, then clear
      // any previous selections from the list.
      remove(currentSelections, currSel => {
        return newSelections.every(newSel => !isEqual(currSel, newSel));
      });
    }
    return currentSelections;
  }

  // Hovers.

  setHover(newHover: Hover | undefined) {
    const currHover = this.queryStore(getActionModeHover);
    if (!isEqual(newHover, currHover)) {
      this.store.dispatch(new SetActionModeHover(newHover));
    }
  }

  splitInHalfHover() {
    const pointSelections = getSelectionsOfType(this.getSelections(), SelectionType.Point);
    if (pointSelections.length) {
      const { source, subIdx, cmdIdx } = pointSelections[0];
      this.setHover({ type: HoverType.Split, source, subIdx, cmdIdx });
    }
  }

  clearHover() {
    this.setHover(undefined);
  }

  // Mutate subpaths.

  reverseSelectedSubPaths() {
    this.mutateSelectedSubPaths((pm, subIdx) => pm.reverseSubPath(subIdx));
  }

  shiftBackSelectedSubPaths() {
    this.mutateSelectedSubPaths((pm, subIdx) => pm.shiftSubPathBack(subIdx));
  }

  shiftForwardSelectedSubPaths() {
    this.mutateSelectedSubPaths((pm, subIdx) => pm.shiftSubPathForward(subIdx));
  }

  private mutateSelectedSubPaths(mutatorFn: (pm: PathMutator, subIdx: number) => void) {
    const selections = getSelectionsOfType(this.getSelections(), SelectionType.SubPath);
    if (!selections.length) {
      // The shortcuts call this in every action mode, even when no subpath is selected.
      return;
    }
    const { source } = selections[0];
    const path = this.getActivePathBlockValue(source);
    if (!path) {
      return;
    }
    const pm = path.mutate();
    for (const { subIdx } of selections) {
      mutatorFn(pm, subIdx);
    }
    this.store.dispatch(
      new BatchAction(
        this.buildUpdatedActivePathBlockAnimationAction(source, pm.build()),
        new SetActionModeHover(undefined),
      ),
    );
  }

  // Mutate points.

  shiftPointToFront() {
    const selections = getSelectionsOfType(this.getSelections(), SelectionType.Point);
    if (!selections.length) {
      return;
    }
    const { source, subIdx, cmdIdx } = selections[0];
    const activePath = this.getActivePathBlockValue(source);
    if (!activePath) {
      return;
    }
    const pm = activePath.mutate();
    pm.shiftSubPathForward(subIdx, cmdIdx);
    this.store.dispatch(this.buildUpdatedActivePathBlockAnimationAction(source, pm.build()));
  }

  splitSelectedPointInHalf() {
    const selections = getSelectionsOfType(this.getSelections(), SelectionType.Point);
    if (!selections.length) {
      return;
    }
    const { source, subIdx, cmdIdx } = selections[0];
    const activePath = this.getActivePathBlockValue(source);
    if (!activePath) {
      return;
    }
    const pm = activePath.mutate();
    pm.splitCommandInHalf(subIdx, cmdIdx);
    this.store.dispatch(
      new BatchAction(
        this.buildUpdatedActivePathBlockAnimationAction(source, pm.build()),
        new SetActionModeSelections([]),
        new SetActionModeHover(undefined),
      ),
    );
  }

  // Pair/unpair subpaths.

  pairSubPath(subIdx: number, actionSource: ActionSource) {
    const currUnpair = this.getUnpairedSubPath();
    const actions: Action[] = [];
    if (currUnpair && actionSource !== currUnpair.source) {
      const { source: fromSource, subIdx: fromSubIdx } = currUnpair;
      const toSource = actionSource;
      const toSubIdx = subIdx;
      const fromPath = this.getActivePathBlockValue(fromSource);
      const toPath = this.getActivePathBlockValue(toSource);
      if (!fromPath || !toPath) {
        return;
      }
      actions.push(new SetUnpairedSubPath(undefined));
      const fromSelections = this.getSelections().filter(s => s.source === fromSource);
      const toSelections = this.getSelections().filter(s => s.source === toSource);
      if (fromSelections.length) {
        actions.push(
          new SetActionModeSelections(
            fromSelections.map(s => ({ ...s, subIdx: s.subIdx === fromSubIdx ? 0 : s.subIdx })),
          ),
        );
      } else if (toSelections.length) {
        actions.push(
          new SetActionModeSelections(
            toSelections.map(s => ({ ...s, subIdx: s.subIdx === toSubIdx ? 0 : s.subIdx })),
          ),
        );
      }
      const pairedSubPaths = new Set<number>();
      this.getPairedSubPaths().forEach(p => pairedSubPaths.add(p));
      if (pairedSubPaths.has(fromSubIdx)) {
        pairedSubPaths.delete(fromSubIdx);
      }
      if (pairedSubPaths.has(toSubIdx)) {
        pairedSubPaths.delete(toSubIdx);
      }
      pairedSubPaths.add(pairedSubPaths.size);
      actions.push(new SetPairedSubPaths(pairedSubPaths));
      actions.push(new SetActionModeHover(undefined));
      let updatedAnimation = this.buildUpdatedActivePathBlockAnimation(
        fromSource,
        fromPath.mutate().moveSubPath(fromSubIdx, 0).build(),
      );
      updatedAnimation = this.buildUpdatedActivePathBlockAnimation(
        toSource,
        toPath.mutate().moveSubPath(toSubIdx, 0).build(),
        updatedAnimation,
      );
      actions.push(new SetAnimation(updatedAnimation));
    } else {
      actions.push(new SetUnpairedSubPath({ source: actionSource, subIdx }));
    }
    this.store.dispatch(new BatchAction(...actions));
  }

  private getPairedSubPaths() {
    return this.queryStore(getPairedSubPaths);
  }

  setUnpairedSubPath(unpair: { subIdx: number; source: ActionSource }) {
    if (!isEqual(this.getUnpairedSubPath(), unpair)) {
      this.store.dispatch(new SetUnpairedSubPath(unpair));
    }
  }

  private getUnpairedSubPath() {
    return this.queryStore(getUnpairedSubPath);
  }

  // Autofix.

  autoFix() {
    const fromPath = this.getActivePathBlockValue(ActionSource.From);
    const toPath = this.getActivePathBlockValue(ActionSource.To);
    if (!fromPath || !toPath) {
      return;
    }
    let from: Path;
    let to: Path;
    try {
      [from, to] = AutoAwesome.autoFix(fromPath, toPath);
    } catch (e) {
      // Trying again would fail the same way, so say so instead of doing nothing.
      bugsnagClient.notify(e instanceof Error ? e : String(e));
      this.snackBarService.show("Couldn't auto fix these paths", 'Dismiss', Duration.Long);
      return;
    }
    let animation = this.buildUpdatedActivePathBlockAnimation(ActionSource.From, from);
    animation = this.buildUpdatedActivePathBlockAnimation(ActionSource.To, to, animation);
    // Auto fix adds points and reorders subpaths, so the selected points, the hover, and the
    // subpaths paired so far in pair subpaths mode may not refer to the same things anymore. It
    // gets an undo step of its own, or clicking it right after double-clicking the block would
    // join the selection's step, and undoing it would deselect the block too.
    this.store.dispatch(
      new BatchAction(
        new IsolateUndoStep(),
        new SetAnimation(animation),
        new SetActionModeSelections([]),
        new SetActionModeHover(undefined),
        new SetUnpairedSubPath(undefined),
        new SetPairedSubPaths(new Set()),
      ),
    );
  }

  // Delete selected action mode models.

  deleteSelectedActionModeModels() {
    if (this.getActionMode() !== ActionMode.Selection) {
      return;
    }
    const selections = this.getSelections();
    if (!selections.length) {
      return;
    }
    const subPathSelections = getSelectionsOfType(selections, SelectionType.SubPath);
    const segmentSelections = getSelectionsOfType(selections, SelectionType.Segment);
    const pointSelections = getSelectionsOfType(selections, SelectionType.Point);
    let updatePathAction: SetAnimation | undefined;
    if (subPathSelections.length) {
      const { source, subIdx } = subPathSelections[0];
      const path = this.getActivePathBlockValue(source);
      if (path?.getSubPath(subIdx).isSplit()) {
        const pm = path.mutate();
        const layer = this.getActivePathBlockLayer();
        if (layer.isFilled()) {
          pm.deleteFilledSubPath(subIdx);
        } else if (layer.isStroked()) {
          pm.deleteStrokedSubPath(subIdx);
        }
        updatePathAction = this.buildUpdatedActivePathBlockAnimationAction(source, pm.build());
      }
    } else if (segmentSelections.length) {
      const { source, subIdx, cmdIdx } = segmentSelections[0];
      const path = this.getActivePathBlockValue(source);
      if (path?.getCommand(subIdx, cmdIdx).isSplitSegment()) {
        updatePathAction = this.buildUpdatedActivePathBlockAnimationAction(
          source,
          path.mutate().deleteFilledSubPathSegment(subIdx, cmdIdx).build(),
        );
      }
    } else if (pointSelections.length) {
      const source = pointSelections[0].source;
      const path = this.getActivePathBlockValue(source);
      const unsplitOpsMap = new Map<number, Array<{ subIdx: number; cmdIdx: number }>>();
      for (const { subIdx, cmdIdx } of pointSelections) {
        if (!path?.getCommand(subIdx, cmdIdx).isSplitPoint()) {
          continue;
        }
        let subIdxOps = unsplitOpsMap.get(subIdx);
        if (!subIdxOps) {
          subIdxOps = [];
        }
        subIdxOps.push({ subIdx, cmdIdx });
        unsplitOpsMap.set(subIdx, subIdxOps);
      }
      if (path && unsplitOpsMap.size) {
        const pm = path.mutate();
        unsplitOpsMap.forEach((ops, idx) => {
          PathUtil.sortPathOps(ops);
          ops.forEach(op => pm.unsplitCommand(op.subIdx, op.cmdIdx));
        });
        updatePathAction = this.buildUpdatedActivePathBlockAnimationAction(source, pm.build());
      }
    }
    if (updatePathAction) {
      this.store.dispatch(
        new BatchAction(
          updatePathAction,
          new SetActionModeSelections([]),
          new SetActionModeHover(undefined),
        ),
      );
    }
  }

  // Update active path block.

  updateActivePathBlock(source: ActionSource, path: Path) {
    this.store.dispatch(this.buildUpdatedActivePathBlockAnimationAction(source, path));
  }

  private buildUpdatedActivePathBlockAnimationAction(
    source: ActionSource,
    path: Path,
    animation = this.layerTimelineService.getAnimation(),
  ) {
    return new SetAnimation(this.buildUpdatedActivePathBlockAnimation(source, path, animation));
  }

  private buildUpdatedActivePathBlockAnimation(
    source: ActionSource,
    path: Path,
    animation = this.layerTimelineService.getAnimation(),
  ) {
    const blockId = this.getRequiredActivePathBlock().id;
    const blockIndex = findIndex(animation.blocks, b => b.id === blockId);
    const block = animation.blocks[blockIndex] as PathAnimationBlock;

    // Remove any existing conversions and collapsing sub paths from the path.
    const oppSource = source === ActionSource.From ? ActionSource.To : ActionSource.From;
    let oppPath = oppSource === ActionSource.From ? block.fromValue : block.toValue;
    if (oppPath) {
      [path, oppPath] = AutoAwesome.autoAddCollapsingSubPaths(path, oppPath);
      [path, oppPath] = AutoAwesome.autoConvert(path, oppPath);
    }

    const setBlockValueFn = (b: PathAnimationBlock, t: ActionSource, p: Path | undefined) => {
      if (t === ActionSource.From) {
        b.fromValue = p;
      } else {
        b.toValue = p;
      }
    };

    const newBlock = block.clone();
    setBlockValueFn(newBlock, source, path);
    setBlockValueFn(newBlock, oppSource, oppPath);

    const newBlocks = animation.blocks.map((b, i) => (i === blockIndex ? newBlock : b));
    animation = animation.clone();
    animation.blocks = newBlocks;
    return animation;
  }

  /**
   * Returns the block being edited in action mode, or undefined if it's gone (e.g. deleted from
   * the timeline), in which case there's nothing to edit.
   */
  private getActivePathBlock() {
    const block = this.layerTimelineService.getSelectedBlocks()[0];
    return block instanceof PathAnimationBlock ? block : undefined;
  }

  private getActivePathBlockValue(source: ActionSource) {
    const activeBlock = this.getActivePathBlock();
    return source === ActionSource.From ? activeBlock?.fromValue : activeBlock?.toValue;
  }

  private getActivePathBlockLayer() {
    const vl = this.layerTimelineService.getVectorLayer();
    return vl.findLayerById(this.getRequiredActivePathBlock().layerId) as MorphableLayer;
  }

  /** Returns the block being edited, for callers that have already found a path in it. */
  private getRequiredActivePathBlock() {
    const block = this.getActivePathBlock();
    if (!block) {
      throw new Error('There is no path block being edited');
    }
    return block;
  }

  private queryStore<T>(selector: (state: State) => T) {
    return selector(this.store.getState());
  }
}
